import { z } from 'zod'
import { xrplClassicAddressSchema, xrplHashSchema } from './address'

/** Mainnet 公開フルヒストリー RPC。任意ネットワークへの切替は提供しない。 */
export const xrplMainnetRpcEndpoint = 'https://s2.ripple.com:51234/'

/** API v2 の確定済み account_tx エントリ。metadata の資産解析は後段で検証する。 */
export const xrplTransactionSchema = z.object({
  validated: z.literal(true),
  ledger_index: z.number().int().positive().safe(),
  hash: xrplHashSchema,
  close_time_iso: z.string().datetime({ offset: true }),
  tx_json: z
    .object({
      Account: xrplClassicAddressSchema,
      TransactionType: z.string().min(1),
      Fee: z.string().regex(/^\d+$/),
    })
    .passthrough(),
  meta: z
    .object({
      TransactionResult: z.string().min(1),
      TransactionIndex: z.number().int().nonnegative().safe(),
      AffectedNodes: z.array(z.record(z.unknown())),
    })
    .passthrough(),
})

/** account_tx の検証済み取引。 */
export type XrplTransaction = z.infer<typeof xrplTransactionSchema>

/** JSON RPC 応答の検索範囲とページ末尾を検証する。 */
const pageSchema = z.object({
  account: xrplClassicAddressSchema,
  ledger_index_min: z.number().int().positive().safe(),
  ledger_index_max: z.number().int().positive().safe(),
  validated: z.literal(true),
  transactions: z.array(xrplTransactionSchema),
  marker: z.record(z.unknown()).optional(),
})

/**
 * 固定 Mainnet RPC から履歴をページ単位に取得する client を作る。
 * @param params 既存認証プロキシ URL、token 取得処理、テスト用 HTTP transport
 * @returns 検証済みページを順次保存できる履歴取得 API
 */
export const createXrplRpcClient = (params: {
  proxyEndpoint: string
  getToken: () => Promise<string | null>
  fetcher?: typeof fetch
}) => {
  /**
   * API v2 の結果を取得し、HTTP/RPC エラーを欠落として上位へ通知する。
   * @param input RPC method とパラメータ
   * @returns 未検証の RPC result
   */
  const request = async (input: {
    method: string
    parameters: Record<string, unknown>
  }): Promise<unknown> => {
    const token = await params.getToken()
    if (!token) throw new Error('XRPL 同期には認証が必要です')
    const url = new URL(params.proxyEndpoint)
    url.searchParams.set('url', xrplMainnetRpcEndpoint)
    const response = await (params.fetcher ?? fetch)(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        method: input.method,
        params: [{ api_version: 2, ...input.parameters }],
      }),
      signal: AbortSignal.timeout(30_000),
    })
    if (!response.ok) throw new Error(`XRPL HTTP ${response.status}`)
    const { result } = z
      .object({ result: z.record(z.unknown()) })
      .parse(await response.json())
    if (result.status !== 'success' || result.error !== undefined)
      throw new Error(
        `XRPL RPC: ${result.error ?? result.status ?? 'invalid response'}`,
      )
    return result
  }
  return {
    /**
     * 開始日時を包含する確定 ledger 範囲を二分探索する。
     * @param input 開始日時。ledgerMin のみで差分、ledgerMax も指定すると保存済み範囲を再検証する。
     * @returns 両端を固定した ledger 範囲
     */
    getLedgerRange: async (input: {
      startTime: number
      ledgerMin?: number
      ledgerMax?: number
    }): Promise<{
      min: number
      max: number
    }> => {
      const startTime = z.number().nonnegative().finite().parse(input.startTime)
      /** 確定 ledger の時刻と番号を検証する。 */
      const ledger = async (value: { index: number | 'validated' }) => {
        const result = z
          .object({
            validated: z.literal(true),
            ledger_index: z.coerce.number().int().positive().safe(),
            ledger: z.object({
              close_time: z.number().int().nonnegative().safe(),
            }),
          })
          .parse(
            await request({
              method: 'ledger',
              parameters: {
                ledger_index: value.index,
                transactions: false,
                expand: false,
              },
            }),
          )
        if (
          typeof value.index === 'number' &&
          result.ledger_index !== value.index
        )
          throw new Error('XRPL ledger mismatch')
        return {
          index: result.ledger_index,
          time: (result.ledger.close_time + 946684800) * 1000,
        }
      }
      const latest = await ledger({ index: 'validated' })
      const server = z
        .object({
          info: z.object({
            network_id: z.literal(0).optional(),
            complete_ledgers: z.string(),
          }),
        })
        .parse(await request({ method: 'server_info', parameters: {} }))
      /** 必要範囲の途中に保持されていない ledger がないことを確認する。 */
      const requireCoverage = (range: { min: number; max: number }) => {
        const intervals = server.info.complete_ledgers
          .split(',')
          .map((text) => {
            if (!/^\d+(?:-\d+)?$/.test(text))
              throw new Error('Invalid XRPL ledger coverage')
            const [start, end = start] = text
              .split('-')
              .map((value) =>
                z.number().int().positive().safe().parse(Number(value)),
              )
            if (end < start) throw new Error('Invalid XRPL ledger coverage')
            return { start, end }
          })
          .sort((a, b) => a.start - b.start)
        let next = range.min
        for (const interval of intervals) {
          if (interval.end < next) continue
          if (interval.start > next) break
          next = interval.end + 1
          if (next > range.max) return range
        }
        throw new Error('XRPL RPC に必要な ledger 履歴の欠落があります')
      }
      if (input.ledgerMin !== undefined) {
        const min = z.number().int().positive().safe().parse(input.ledgerMin)
        const max = z
          .number()
          .int()
          .min(min)
          .max(latest.index)
          .safe()
          .parse(input.ledgerMax ?? latest.index)
        return requireCoverage({ min, max })
      }
      if (input.ledgerMax !== undefined)
        throw new Error('XRPL ledgerMin is required with ledgerMax')
      // Mainnet の現存履歴は ledger 32570 から始まる。
      const earliest = await ledger({ index: 32570 })
      if (startTime === 0)
        return requireCoverage({ min: earliest.index, max: latest.index })
      if (earliest.time > startTime)
        throw new Error('XRPL の開始日時までの履歴がありません')
      let low = earliest.index
      let high = latest.index
      while (low < high) {
        const middle = Math.floor((low + high) / 2)
        const current = await ledger({ index: middle })
        if (current.time < startTime) low = middle + 1
        else high = middle
      }
      return requireCoverage({ min: low, max: latest.index })
    },
    /**
     * 取引時点の MPT issuance から不変の数量スケールを取得する。
     * @param input 発行 ID と対象 ledger
     * @returns 検証済み AssetScale
     */
    getMptScale: async (input: {
      issuanceId: string
      ledgerIndex: number
    }): Promise<number> => {
      const id = z
        .string()
        .regex(/^[A-Fa-f0-9]{48}$/)
        .parse(input.issuanceId)
      const index = z.number().int().positive().safe().parse(input.ledgerIndex)
      const result = z
        .object({
          ledger_index: z.number().int().positive().safe(),
          node: z.object({
            LedgerEntryType: z.literal('MPTokenIssuance'),
            AssetScale: z.number().int().min(0).max(255).default(0),
          }),
        })
        .parse(
          await request({
            method: 'ledger_entry',
            parameters: { mpt_issuance: id, ledger_index: index },
          }),
        )
      if (result.ledger_index !== index)
        throw new Error('XRPL MPT ledger mismatch')
      return result.node.AssetScale
    },
    /**
     * 確定範囲を固定して全ページを読み、範囲縮小・循環 marker・未確定・順序異常を拒否する。
     * @param input アカウント、必要な最小・最大 ledger、保存 callback
     * @returns 必要な範囲が完全取得された場合のみ解決する Promise
     */
    listTransactions: async (input: {
      address: string
      ledgerMin: number
      ledgerMax: number
      marker?: Record<string, unknown>
      onPage: (page: {
        transactions: readonly XrplTransaction[]
        nextMarker?: Record<string, unknown>
      }) => Promise<void>
    }): Promise<void> => {
      const address = xrplClassicAddressSchema.parse(input.address)
      const min = z.number().int().positive().safe().parse(input.ledgerMin)
      const max = z.number().int().min(min).safe().parse(input.ledgerMax)
      const markers = new Set<string>()
      const hashes = new Set<string>()
      let marker =
        input.marker === undefined
          ? undefined
          : z.record(z.unknown()).parse(input.marker)
      if (marker !== undefined)
        markers.add(
          JSON.stringify(
            Object.entries(marker).sort(([a], [b]) => a.localeCompare(b)),
          ),
        )
      let previousLedger = min
      let previousIndex = -1
      do {
        const page = pageSchema.parse(
          await request({
            method: 'account_tx',
            parameters: {
              account: address,
              ledger_index_min: min,
              ledger_index_max: max,
              forward: true,
              binary: false,
              limit: 200,
              ...(marker ? { marker } : {}),
            },
          }),
        )
        if (
          page.account !== address ||
          page.ledger_index_min !== min ||
          page.ledger_index_max !== max
        )
          throw new Error('XRPL の必要な履歴範囲を取得できませんでした')
        for (const transaction of page.transactions.sort(
          (a, b) =>
            a.ledger_index - b.ledger_index ||
            a.meta.TransactionIndex - b.meta.TransactionIndex,
        )) {
          if (
            transaction.ledger_index < min ||
            transaction.ledger_index > max ||
            transaction.ledger_index < previousLedger ||
            (transaction.ledger_index === previousLedger &&
              transaction.meta.TransactionIndex <= previousIndex) ||
            hashes.has(transaction.hash)
          )
            throw new Error('XRPL transaction pagination is inconsistent')
          hashes.add(transaction.hash)
          previousLedger = transaction.ledger_index
          previousIndex = transaction.meta.TransactionIndex
        }
        if (page.marker !== undefined) {
          const key = JSON.stringify(
            Object.entries(page.marker).sort(([a], [b]) => a.localeCompare(b)),
          )
          if (markers.has(key) || page.transactions.length === 0)
            throw new Error('XRPL pagination marker did not advance')
          markers.add(key)
        }
        await input.onPage({
          transactions: page.transactions,
          nextMarker: page.marker,
        })
        marker = page.marker
      } while (marker !== undefined)
    },
  }
}
